// Copyright (C) AirWorks Solutions, Inc - All Rights Reserved
// DO NOT REDISTRIBUTE
// UNAUTHORIZED COPYING OF THIS FILE, ANY PART OR WHOLE, VIA ANY MEDIUM IS STRICTLY PROHIBITED
// PROPRIETARY AND CONFIDENTIAL

// Turn a user-picked file (a site photo or a PDF page) into a base64 ChatImageBlock that rides the SAME chat
// path as a pano frame — Khora answers about it with vision, cross-referencing the gdb. Images downscale on a
// canvas to the model's working size (1568 px, matching panoImage.ts); PDFs render their first page with pdf.js
// on the client (fork: client-side rasterize) so the Lambda stays image-only. pdf.js is lazily imported so any
// bundler/worker issue degrades ONLY the PDF path — plain image upload never depends on it.

import type { ChatImageBlock } from './chatbotTypes';

const MAX_EDGE_PX = 1568; // Anthropic downscales vision beyond this anyway; same size a pano frame uses.
const JPEG_QUALITY = 0.85;
const MAX_ATTACH_BYTES = 25 * 1024 * 1024; // reject an absurd file up front, before any decode
const PDF_RENDER_SCALE = 2; // ~150 DPI raster before the downscale, enough to read plan-sheet callouts

export type AttachmentKind = 'image' | 'pdf';

export interface Attachment {
  block: ChatImageBlock;
  name: string;
  kind: AttachmentKind;
  previewUrl: string; // a data: URL for the compose-row thumbnail (the block, re-wrapped)
}

export interface AttachmentError {
  error: string;
}

export const isAttachmentError = (value: Attachment | AttachmentError): value is AttachmentError =>
  (value as AttachmentError).error !== undefined;

/** Draw a decoded source scaled to <= MAX_EDGE_PX onto a canvas and encode it as a base64 JPEG image block. */
const encodeScaled = (source: CanvasImageSource, width: number, height: number): ChatImageBlock | null => {
  const scale = Math.min(1, MAX_EDGE_PX / Math.max(width, height));
  const outWidth = Math.max(1, Math.round(width * scale));
  const outHeight = Math.max(1, Math.round(height * scale));
  const canvas = document.createElement('canvas');
  canvas.width = outWidth;
  canvas.height = outHeight;
  const context = canvas.getContext('2d');
  if (!context) return null;
  context.drawImage(source, 0, 0, outWidth, outHeight);
  const data = canvas.toDataURL('image/jpeg', JPEG_QUALITY).split(',', 2)[1];
  return data ? { type: 'image', source: { type: 'base64', media_type: 'image/jpeg', data } } : null;
};

/** Decode an image file via an <img> element, then downscale + re-encode. */
const imageFileToBlock = (file: File): Promise<ChatImageBlock | null> => new Promise((resolve) => {
  const url = URL.createObjectURL(file);
  const image = new Image();
  image.onload = () => {
    const block = encodeScaled(image, image.naturalWidth, image.naturalHeight);
    URL.revokeObjectURL(url);
    resolve(block);
  };
  image.onerror = () => { URL.revokeObjectURL(url); resolve(null); };
  image.src = url;
});

/** Render the FIRST page of a PDF to a canvas with pdf.js (lazy import), then downscale + re-encode. */
const pdfFirstPageToBlock = async (file: File): Promise<ChatImageBlock | null> => {
  const pdfjs: any = await import('pdfjs-dist');
  // The worker ships as a static file copied to the app root by webpack (Cesium-style; see webpack.config.js).
  // pdf.js 4.x ships the worker as an ES module (.mjs); the browser loads it as a module worker.
  pdfjs.GlobalWorkerOptions.workerSrc = '/pdf.worker.min.mjs';
  const buffer = await file.arrayBuffer();
  const document_ = await pdfjs.getDocument({ data: buffer }).promise;
  try {
    const page = await document_.getPage(1); // first slice: page 1 only (a page picker is a follow-on)
    const viewport = page.getViewport({ scale: PDF_RENDER_SCALE });
    const canvas = document.createElement('canvas');
    canvas.width = Math.ceil(viewport.width);
    canvas.height = Math.ceil(viewport.height);
    const context = canvas.getContext('2d');
    if (!context) return null;
    await page.render({ canvasContext: context, viewport }).promise;
    return encodeScaled(canvas, canvas.width, canvas.height);
  } finally {
    document_.destroy?.();
  }
};

/**
 * Convert a picked file into an Attachment (or a friendly AttachmentError). Accepts images and PDFs; anything
 * else, an over-large file, or a decode failure returns an error the caller shows in a snackbar. Never throws.
 */
export const fileToAttachment = async (file: File): Promise<Attachment | AttachmentError> => {
  if (file.size > MAX_ATTACH_BYTES) {
    return { error: `That file is too large (${Math.round(file.size / (1024 * 1024))} MB). The limit is 25 MB.` };
  }
  const isPdf = file.type === 'application/pdf' || file.name.toLowerCase().endsWith('.pdf');
  const isImage = file.type.startsWith('image/');
  if (!isImage && !isPdf) {
    return { error: 'Please attach a photo (JPG/PNG) or a PDF page.' };
  }
  try {
    const block = isPdf ? await pdfFirstPageToBlock(file) : await imageFileToBlock(file);
    if (!block) {
      return { error: isPdf
        ? "Couldn't render that PDF here — try attaching an image of the page instead."
        : "Couldn't read that image." };
    }
    return {
      block,
      name: file.name,
      kind: isPdf ? 'pdf' : 'image',
      previewUrl: `data:${block.source.media_type};base64,${block.source.data}`,
    };
  } catch {
    return { error: isPdf
      ? "Couldn't render that PDF here — try attaching an image of the page instead."
      : "Couldn't read that file." };
  }
};
